export default (element: HTMLElement) => {
  // Find all necessary elements
  const map = element.querySelector<SVGElement>('[data-component-role="map"]');
  const tooltip = element.querySelector<HTMLElement>(
    '[data-component-role="tooltip"]',
  );
  const tooltipName = tooltip?.querySelector<HTMLElement>(
    '.racesOverview__tooltipName',
  );
  const tooltipCounts = tooltip?.querySelector<HTMLElement>(
    '.racesOverview__tooltipCounts',
  );
  const regions = element.querySelectorAll<HTMLElement>(
    '[data-component-role="region"]',
  );
  const regionHeaders = element.querySelectorAll<HTMLElement>(
    '[data-component-role="regionHeader"]',
  );

  if (!map) {
    console.error('Map element not found');
    return;
  }

  if (!tooltip || !tooltipName || !tooltipCounts) {
    console.error('Tooltip elements not found');
    return;
  }

  if (regions.length === 0) {
    console.error('No region elements found');
    return;
  }

  // Get all map paths
  const paths = map.querySelectorAll<SVGPathElement>('.racesOverview__path');

  // Helper: Normalize slug from hash (remove # and any extra characters)
  const getSlugFromHash = (): string | null => {
    const hash = window.location.hash.slice(1); // Remove #
    return hash || null;
  };

  // Helper: Set URL hash without triggering scroll
  const setHash = (slug: string) => {
    history.pushState(null, '', `#${slug}`);
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  };

  // Helper: Update map highlighting based on active slug
  const updateMapHighlight = (activeSlug: string | null) => {
    paths.forEach((path) => {
      const pathSlug = path.dataset.regionSlug;
      path.classList.toggle(
        'racesOverview__path--active',
        pathSlug === activeSlug,
      );
    });
  };

  // Helper: Update accordion state based on active slug
  const updateAccordionState = (activeSlug: string | null) => {
    regions.forEach((region) => {
      const regionSlug = region.dataset.regionSlug;
      region.classList.toggle(
        'racesOverview__region--collapsed',
        regionSlug !== activeSlug,
      );
    });
  };

  // Helper: Scroll to active region
  const scrollToActiveRegion = (slug: string) => {
    const region = Array.from(regions).find(
      (r) => r.dataset.regionSlug === slug,
    );
    if (region) {
      region.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Helper: Sync both map and accordion based on hash
  const syncFromHash = (shouldScroll: boolean = false) => {
    const slug = getSlugFromHash();
    updateMapHighlight(slug);
    updateAccordionState(slug);
    if (shouldScroll && slug) {
      scrollToActiveRegion(slug);
    }
  };

  // Map path hover handlers
  paths.forEach((path) => {
    path.addEventListener('mouseenter', () => {
      const slug = path.dataset.regionSlug;
      const region = Array.from(regions).find(
        (r) => r.dataset.regionSlug === slug,
      );
      const regionName = region?.dataset.regionName;
      const subtitle = region?.dataset.regionSubtitle;

      if (tooltipName) {
        tooltipName.textContent = regionName ?? path.dataset.region ?? '';
      }

      if (tooltipCounts) {
        tooltipCounts.textContent =
          subtitle ?? 'V tomto kraji nejsou žádné závody';
      }

      tooltip.classList.add('racesOverview__tooltip--visible');
    });

    path.addEventListener('mouseleave', () => {
      tooltip.classList.remove('racesOverview__tooltip--visible');
    });

    path.addEventListener('mousemove', (e) => {
      if (!tooltip) return;

      const offsetX = 15;
      const offsetY = 15;
      let x = e.clientX + offsetX;
      let y = e.clientY + offsetY;

      // Prevent tooltip from going off screen
      const tooltipRect = tooltip.getBoundingClientRect();
      if (x + tooltipRect.width > window.innerWidth) {
        x = e.clientX - tooltipRect.width - offsetX;
      }
      if (y + tooltipRect.height > window.innerHeight) {
        y = e.clientY - tooltipRect.height - offsetY;
      }

      tooltip.style.left = `${x}px`;
      tooltip.style.top = `${y}px`;
    });

    path.addEventListener('click', () => {
      const slug = path.dataset.regionSlug;
      if (slug) {
        setHash(slug);
        setTimeout(() => scrollToActiveRegion(slug), 100);
      }
    });
  });

  // Accordion header click handlers
  regionHeaders.forEach((header) => {
    header.addEventListener('click', () => {
      const region = header.closest<HTMLElement>(
        '[data-component-role="region"]',
      );
      if (!region) return;

      const slug = region.dataset.regionSlug;
      if (!slug) return;

      const currentSlug = getSlugFromHash();

      // Toggle: if clicking the same region, close it (clear hash)
      if (currentSlug === slug) {
        history.pushState(null, '', window.location.pathname);
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } else {
        setHash(slug);
      }
    });
  });

  // Listen to hash changes (from browser back/forward, or programmatic changes)
  window.addEventListener('hashchange', () => {
    syncFromHash(false);
  });

  // Initialize on page load
  const initialSlug = getSlugFromHash();
  if (!initialSlug && regions.length > 0) {
    // No hash: open first region by default
    const firstSlug = regions[0].dataset.regionSlug;
    if (firstSlug) {
      setHash(firstSlug);
    }
  } else {
    syncFromHash(false);
  }
};
